import { createFileRoute } from "@tanstack/react-router"
import { useVariantContext } from "~/context/VariantProvider"
import { InventoryGraphCard } from "~/components/home/InventoryGraphCard"
import { useMemo, useState } from "react"
import { useSkuStore } from "~/providers/sku-store-provider"
import VariantTableCard, {
  VariantsTableRow,
} from "~/components/product/VariantTableCard/VariantTableCard"
import { usePageTracking } from "~/hooks/usePageTracking"
import { InventoryComparisonCard } from "~/components/forecast/InventoryComparisonCard"
import { api } from "~/utils/api"
import { CardTitle } from "~/components/reusable/Card"
import {
  ExpandableCard,
  ExpandableCardContent,
  ExpandableCardHeader,
} from "~/components/design-system/components"
import { StorageLocationCard } from "~/components/inventory/StorageLocationCard"
import { AnalysisPage } from "~/components/design-system/pages"
import VariantInspector from "~/components/product/VariantInspector"

export const Route = createFileRoute("/_auth/product/$productId/_layout/inventory")({
  component: RouteComponent,
})

function RouteComponent() {
  usePageTracking("Product - Inventory")

  const { variants, isMonthly, startDate, endDate } = useVariantContext()
  const variantIds = useMemo(() => variants.map((v) => v.variantId), [variants])

  const selectedWarehouseId = useSkuStore((state) => state.warehouseId)
  const selectedChannel = useSkuStore((state) => state.channel)

  const [expandedWarehouseIds, setExpandedWarehouseIds] = useState<string[]>([])

  const { data: warehouses } = api.warehouse.get.useQuery()
  const { data: locations } = api.storageLocation.get.useQuery()

  return (
    <AnalysisPage
      inspector={
        <VariantInspector sections={["image", "details", "suggestedOrders", "comments"]} />
      }
    >
      <div className="min-h-full flex flex-col gap-4">
        <InventoryGraphCard
          variantIds={variantIds}
          warehouseId={selectedWarehouseId}
          channel={selectedChannel}
          isMonthly={isMonthly}
          startDate={startDate}
          endDate={endDate}
          graphHeight={400}
        />
        <VariantTableCard
          variantIds={variantIds}
          monthlyView={isMonthly}
          startDate={startDate}
          endDate={endDate}
          warehouseId={selectedWarehouseId}
          channel={selectedChannel}
          rows={[
            VariantsTableRow.ClosingStock,
            VariantsTableRow.ForecastedReturns,
            VariantsTableRow.SuggestedOrders,
            VariantsTableRow.SuggestedIncomingStock,
            VariantsTableRow.WeeksOnHand,
            VariantsTableRow.IncomingStock,
            VariantsTableRow.ArrivedStock,
            VariantsTableRow.Availability,
            VariantsTableRow.OptimalStock,
          ]}
        />

        {warehouses &&
          warehouses.length > 1 &&
          warehouses
            .filter((w) => w.id !== selectedWarehouseId)
            .map((warehouse) => (
              <ExpandableCard
                key={warehouse.id}
                onExpandedChange={(expanded) => {
                  if (expanded) {
                    setExpandedWarehouseIds([...expandedWarehouseIds, warehouse.id])
                  } else {
                    setExpandedWarehouseIds(
                      expandedWarehouseIds.filter((id) => id !== warehouse.id),
                    )
                  }
                }}
              >
                <ExpandableCardHeader>
                  <CardTitle>{warehouse.name}</CardTitle>
                </ExpandableCardHeader>
                <ExpandableCardContent>
                  {expandedWarehouseIds.includes(warehouse.id) && (
                    <VariantTableCard
                      variantIds={variantIds}
                      monthlyView={isMonthly}
                      startDate={startDate}
                      endDate={endDate}
                      warehouseId={warehouse.id}
                      channel={selectedChannel}
                      rows={[
                        VariantsTableRow.ClosingStock,
                        VariantsTableRow.ForecastedReturns,
                        VariantsTableRow.SuggestedOrders,
                        VariantsTableRow.SuggestedIncomingStock,
                        VariantsTableRow.WeeksOnHand,
                        VariantsTableRow.IncomingStock,
                        VariantsTableRow.ArrivedStock,
                        VariantsTableRow.Availability,
                      ]}
                    />
                  )}
                </ExpandableCardContent>
              </ExpandableCard>
            ))}

        <div className="w-fit">
          <InventoryComparisonCard variantIds={variantIds} />
        </div>

        {locations && <StorageLocationCard variants={variants} />}
      </div>
    </AnalysisPage>
  )
}
